import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { Heading, LoadingSpinner, PageContainer, Section } from '../../../shared/ui';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import { useYowanAuth } from '../app/YowanAuthContext';
import { useYowanAuthGuard } from '../app/yowanAuthGuards';
import {
  createYowanCurrentMemberProfile,
  fetchYowanCurrentMemberProfile,
  isYowanMemberProfileNotFoundError,
  updateYowanCurrentMemberProfile,
} from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');
const { getYowanRouteMeta } = require('../routes/manifest');

const buildProfileFormValues = (profile = {}) => ({
  name: profile.name || '',
  name_kana: profile.nameKana || '',
  phone: profile.phone || '',
  postal_code: profile.postalCode || '',
  address: profile.address || '',
  note: profile.note || '',
});

const getOperatorAccessSummary = (auth = {}) =>
  auth?.accessMe?.operator_access || auth?.accessMe?.operatorAccess || null;

const isOperatorProfileMissing = (auth = {}) => {
  const operatorAccess = getOperatorAccessSummary(auth);

  return Boolean(operatorAccess?.profile_missing === true || operatorAccess?.profileMissing === true);
};

const getOperatorAccessEmail = (auth = {}) => {
  const operatorAccess = getOperatorAccessSummary(auth);

  return operatorAccess?.auth_subject_email || operatorAccess?.authSubjectEmail || '';
};

const getProfileMutationErrorMessage = (error, formContent, isCreateMode) => {
  const code = error?.response?.data?.code;

  if (code && formContent.errorMessagesByCode?.[code]) {
    return formContent.errorMessagesByCode[code];
  }

  return isCreateMode ? formContent.profileCreateErrorMessage : formContent.profileUpdateErrorMessage;
};

const MyPageProfileUpdatePage = ({ guardOptions }) => {
  const content = yowanPageContent['/mypage/profile'];
  const metadata = getYowanRouteMeta('/mypage/profile');
  const location = useLocation();
  const navigate = useNavigate();
  const { syncAuth } = useYowanAuth();
  const guard = useYowanAuthGuard({
    requiresAuth: true,
    search: location.search,
    ...guardOptions,
  });
  const currentUser = guard.currentUser;
  const [submitMessage, setSubmitMessage] = useState('');
  const [memberProfile, setMemberProfile] = useState(null);
  const [profileLoadError, setProfileLoadError] = useState('');
  const [isProfileMissing, setIsProfileMissing] = useState(false);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [isProfileSubmitting, setIsProfileSubmitting] = useState(false);
  const operatorProfileMissing = isOperatorProfileMissing(guard.auth);
  const operatorAccessEmail = getOperatorAccessEmail(guard.auth);
  const canCreateProfile = guard.status === 'ready' && isProfileMissing && operatorProfileMissing;
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: '',
      name_kana: '',
      phone: '',
      postal_code: '',
      address: '',
      note: '',
    },
  });

  useEffect(() => {
    if (!metadata) {
      return;
    }

    document.title = metadata.title;

    if (metadata.description) {
      const descriptionTag = document.querySelector('meta[name="description"]');
      if (descriptionTag) {
        descriptionTag.setAttribute('content', metadata.description);
      }
    }
  }, [metadata]);

  useEffect(() => {
    if (guard.status !== 'ready') {
      return undefined;
    }

    let isMounted = true;

    const loadProfile = async () => {
      setIsProfileLoading(true);
      setProfileLoadError('');
      setIsProfileMissing(false);
      setMemberProfile(null);
      setSubmitMessage('');

      try {
        const profile = await fetchYowanCurrentMemberProfile();
        if (!isMounted) {
          return;
        }

        setMemberProfile(profile);
        reset(buildProfileFormValues(profile));
      } catch (error) {
        if (!isMounted) {
          return;
        }

        reset(buildProfileFormValues());
        if (isYowanMemberProfileNotFoundError(error)) {
          setIsProfileMissing(true);
        } else {
          setProfileLoadError(content.form.profileLoadErrorMessage);
        }
      } finally {
        if (isMounted) {
          setIsProfileLoading(false);
        }
      }
    };

    loadProfile();

    return () => {
      isMounted = false;
    };
  }, [
    content.form.profileLoadErrorMessage,
    guard.status,
    reset,
  ]);

  const onSubmit = async (values) => {
    if (guard.status !== 'ready') {
      setSubmitMessage(content.form.pendingMessage);
      return;
    }

    const isCreateMode = isProfileMissing && operatorProfileMissing;

    if (isProfileMissing && !isCreateMode) {
      setSubmitMessage(content.form.memberMissingMessage);
      return;
    }

    setIsProfileSubmitting(true);
    setSubmitMessage('');
    setProfileLoadError('');

    try {
      if (isCreateMode) {
        const profile = await createYowanCurrentMemberProfile(values);

        setMemberProfile(profile);
        setIsProfileMissing(false);
        reset(buildProfileFormValues(profile));
        setSubmitMessage(content.form.profileCreatedMessage);

        try {
          await syncAuth();
        } catch (syncError) {
          // Profile creation succeeded; the next guard sync can recover if the auth refresh fails here.
        }

        navigate(appendYowanPreviewQueryIfNeeded('/mypage', location.search));
        return;
      }

      const profile = await updateYowanCurrentMemberProfile(values);

      setMemberProfile(profile);
      reset(buildProfileFormValues(profile));
      setSubmitMessage(content.form.profileUpdatedMessage);

      try {
        await syncAuth();
      } catch (syncError) {
        // Profile update succeeded; the next guard sync can recover if the auth refresh fails here.
      }

      navigate(appendYowanPreviewQueryIfNeeded('/mypage', location.search));
    } catch (error) {
      setProfileLoadError(getProfileMutationErrorMessage(error, content.form, isCreateMode));
    } finally {
      setIsProfileSubmitting(false);
    }
  };

  const shouldShowProfileFields =
    guard.status === 'ready' && !isProfileLoading && !profileLoadError && (!isProfileMissing || canCreateProfile);
  const isProfileFormDisabled = isProfileSubmitting;
  const displayEmail = memberProfile?.email || operatorAccessEmail || currentUser.email || content.form.readOnlyEmail;
  const isCreateOptionalProfileFields = canCreateProfile;
  const optionalFieldValidation = isCreateOptionalProfileFields ? {} : { required: content.form.errors.required };
  const profileMessage = canCreateProfile
    ? content.form.operatorProfileCreateMessage
    : content.form.memberMissingMessage;
  const submitLabel = canCreateProfile ? content.form.createSubmitLabel : content.form.submitLabel;
  const pageTitle = canCreateProfile ? content.header.createTitle || content.header.title : content.header.title;

  return (
    <Section>
      <PageContainer>
        <div className="yowan-mypage-update-shell">
          <div className="yowan-mypage-update-header">
            <Heading as="h1" align="left">
              {pageTitle}
            </Heading>
            {content.header.paragraphs.map((paragraph) => (
              <p key={paragraph} className="yowan-mypage-update-header__lead">
                {paragraph}
              </p>
            ))}
            {guard.notice ? (
              <p className="yowan-mypage-update-header__lead">
                {guard.notice.message}
                <br />
                <Link to={guard.notice.actionHref}>{guard.notice.actionLabel}</Link>
              </p>
            ) : null}
            {isProfileMissing ? (
              <p className="yowan-mypage-update-header__lead">
                {profileMessage}
              </p>
            ) : null}
          </div>

          <form className="yowan-mypage-update-form c-form c-surface-content" onSubmit={handleSubmit(onSubmit)}>
            {isProfileLoading ? <LoadingSpinner size="sm" /> : null}
            {profileLoadError ? (
              <p className="yowan-mypage-update-form__error">{profileLoadError}</p>
            ) : null}
            {isProfileMissing ? (
              <p className="yowan-mypage-update-form__message">{profileMessage}</p>
            ) : null}

            {shouldShowProfileFields ? (
              <>
                <label className="c-form__field">
                  <span className="c-form__label">
                    {content.form.labels.name}
                    <em className="c-form__required">*</em>
                  </span>
                  <input
                    className="c-form__control"
                    type="text"
                    disabled={isProfileFormDisabled}
                    {...register('name', { required: content.form.errors.required })}
                  />
                  {errors.name ? <em className="yowan-mypage-update-form__error">{errors.name.message}</em> : null}
                </label>

                <label className="c-form__field">
                  <span className="c-form__label">{content.form.labels.nameKana}</span>
                  <input
                    className="c-form__control"
                    type="text"
                    disabled={isProfileFormDisabled}
                    {...register('name_kana')}
                  />
                </label>

                <label className="c-form__field">
                  <span className="c-form__label">
                    {content.form.labels.phone}
                    {!isCreateOptionalProfileFields ? <em className="c-form__required">*</em> : null}
                  </span>
                  <input
                    className="c-form__control"
                    type="tel"
                    disabled={isProfileFormDisabled}
                    {...register('phone', optionalFieldValidation)}
                  />
                  {errors.phone ? <em className="yowan-mypage-update-form__error">{errors.phone.message}</em> : null}
                </label>

                <label className="c-form__field">
                  <span className="c-form__label">{content.form.labels.email}</span>
                  <input
                    className="c-form__control"
                    type="email"
                    value={displayEmail}
                    disabled
                    readOnly
                  />
                  <span className="yowan-mypage-update-form__help">{content.form.emailHelpText}</span>
                </label>

                <label className="c-form__field">
                  <span className="c-form__label">
                    {content.form.labels.postalCode}
                    {!isCreateOptionalProfileFields ? <em className="c-form__required">*</em> : null}
                  </span>
                  <input
                    className="c-form__control"
                    type="text"
                    disabled={isProfileFormDisabled}
                    {...register('postal_code', optionalFieldValidation)}
                  />
                  {errors.postal_code ? (
                    <em className="yowan-mypage-update-form__error">{errors.postal_code.message}</em>
                  ) : null}
                </label>

                <label className="c-form__field">
                  <span className="c-form__label">
                    {content.form.labels.address}
                    {!isCreateOptionalProfileFields ? <em className="c-form__required">*</em> : null}
                  </span>
                  <input
                    className="c-form__control"
                    type="text"
                    disabled={isProfileFormDisabled}
                    {...register('address', optionalFieldValidation)}
                  />
                  {errors.address ? (
                    <em className="yowan-mypage-update-form__error">{errors.address.message}</em>
                  ) : null}
                </label>

                <label className="c-form__field">
                  <span className="c-form__label">{content.form.labels.note}</span>
                  <textarea
                    className="c-form__control c-form__control--textarea"
                    rows="7"
                    disabled={isProfileFormDisabled}
                    {...register('note')}
                  />
                </label>

              </>
            ) : null}

            {guard.status === 'ready' && shouldShowProfileFields ? (
              <button
                type="submit"
                className="c-form__submit yowan-mypage-update-form__submit"
                disabled={isProfileSubmitting}
              >
                {submitLabel}
              </button>
            ) : null}
            {guard.status !== 'ready' ? (
              <p className="yowan-mypage-update-form__message">
                {content.form.authPendingMessage}
              </p>
            ) : null}

            {submitMessage ? (
              <p className="yowan-mypage-update-form__message">{submitMessage}</p>
            ) : null}
          </form>
        </div>
      </PageContainer>
    </Section>
  );
};

export default MyPageProfileUpdatePage;
